import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ArrowRight, CheckCircle2, Zap, Lock, Sparkles } from "lucide-react";

import { FoxMark, Wordmark } from "@/components/Logo";
import useDocumentMeta from "@/lib/useDocumentMeta";

const fadeUp = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: "easeOut" },
};

function scorePassword(pw) {
    if (!pw) return { score: 0, label: "" };
    let s = 0;
    if (pw.length >= 8) s += 1;
    if (pw.length >= 12) s += 1;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s += 1;
    if (/\d/.test(pw)) s += 1;
    if (/[^A-Za-z0-9]/.test(pw)) s += 1;
    const labels = ["", "Weak", "Fair", "Good", "Strong", "Excellent"];
    return { score: s, label: labels[s] || "" };
}

export default function Register() {
    useDocumentMeta({
        title: "Create account - RecipeFox AI",
        description: "Free RecipeFox AI account. Bring your own AI provider keys and start publishing recipe articles to WordPress in minutes.",
        noIndex: true,
    });
    const { register } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const pwScore = useMemo(() => scorePassword(password), [password]);
    const scoreColor = ["bg-line", "bg-red-400", "bg-amber-400", "bg-amber-500", "bg-green-500", "bg-green-600"][pwScore.score];

    const onSubmit = async (e) => {
        e.preventDefault();
        if (pwScore.score < 2) {
            toast.error("Use a longer or stronger password (8+ chars, mix letters & numbers).");
            return;
        }
        setLoading(true);
        try {
            await register(email, password, name);
            toast.success("Account created. Welcome!");
            nav("/generate");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-paper" data-testid="register-page">
            {/* Left editorial side */}
            <div className="hidden lg:flex w-[44%] bg-ink text-paper p-14 flex-col justify-between relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage: "linear-gradient(#FDFBF7 1px, transparent 1px), linear-gradient(90deg, #FDFBF7 1px, transparent 1px)",
                        backgroundSize: "32px 32px",
                    }} />
                <motion.div {...fadeUp}>
                    <Link to="/" className="flex items-center gap-3 group">
                        <FoxMark size={42} color="#FDFBF7" />
                        <Wordmark className="text-paper text-xl group-hover:text-klein transition-colors" />
                    </Link>
                </motion.div>

                <motion.div {...fadeUp} transition={{ duration: 0.55, delay: 0.1 }}>
                    <div className="overline text-paper/60 mb-6">Get started / 02</div>
                    <h1 className="heading text-5xl xl:text-6xl leading-[0.95]">
                        Free account.{" "}<br />Free forever.{" "}<br />
                        <span className="text-klein">Bring your own AI keys.</span>
                    </h1>
                    <div className="mt-12 grid grid-cols-1 gap-4">
                        {[
                            { icon: Zap, t: "Set up in 5 minutes", b: "Add your Anthropic + kie.ai keys, connect WordPress, generate your first recipe." },
                            { icon: Lock, t: "Your data, your keys", b: "API keys encrypted with Fernet/AES. No prompts logged. GDPR-friendly delete on demand." },
                            { icon: Sparkles, t: "Built for food blogs", b: "AEO/GEO SEO rules, internal linking, WPRM + WP Delicious, full Schema.org markup." },
                        ].map((f) => (
                            <div key={f.t} className="flex gap-3">
                                <div className="w-9 h-9 flex-shrink-0 bg-klein/15 flex items-center justify-center">
                                    <f.icon className="w-4 h-4 text-klein" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold">{f.t}</div>
                                    <div className="text-xs text-paper/70 leading-relaxed mt-0.5">{f.b}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div {...fadeUp} transition={{ duration: 0.55, delay: 0.2 }}
                    className="border-l-2 border-klein pl-4">
                    <p className="text-xs font-mono uppercase text-paper/50 tracking-wider">No credit card. No trial limit.</p>
                    <p className="text-sm text-paper/90 mt-1">You pay AI providers directly - we never touch your billing.</p>
                </motion.div>
            </div>

            {/* Right form */}
            <div className="flex-1 flex flex-col">
                <div className="lg:hidden p-6 border-b border-line">
                    <Link to="/" className="flex items-center gap-3">
                        <FoxMark size={32} color="#111111" />
                        <Wordmark className="text-base" />
                    </Link>
                </div>
                <motion.div {...fadeUp} transition={{ duration: 0.55, delay: 0.15 }}
                    className="flex-1 flex items-center justify-center p-8">
                    <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="register-form">
                        <div>
                            <div className="overline">Create account</div>
                            <h2 className="heading text-3xl mt-2">Two minutes,<br />then you&apos;re publishing.</h2>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="name" className="overline">Name</Label>
                                <Input id="name" type="text" required value={name}
                                    autoFocus autoComplete="name"
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Jamie Oliver"
                                    className="mt-1.5 rounded-none border-line h-11 focus-visible:border-klein focus-visible:ring-0"
                                    data-testid="register-name-input" />
                            </div>
                            <div>
                                <Label htmlFor="email" className="overline">Email</Label>
                                <Input id="email" type="email" required value={email}
                                    autoComplete="email"
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@yourblog.com"
                                    className="mt-1.5 rounded-none border-line h-11 focus-visible:border-klein focus-visible:ring-0"
                                    data-testid="register-email-input" />
                            </div>
                            <div>
                                <Label htmlFor="password" className="overline">Password</Label>
                                <div className="relative mt-1.5">
                                    <Input id="password" type={show ? "text" : "password"} required value={password}
                                        autoComplete="new-password" minLength={8}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="8+ characters"
                                        className="rounded-none border-line h-11 pr-11 focus-visible:border-klein focus-visible:ring-0"
                                        data-testid="register-password-input" />
                                    <button type="button" onClick={() => setShow(!show)}
                                        className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-ink2 hover:text-ink"
                                        aria-label={show ? "Hide password" : "Show password"}
                                        data-testid="register-password-toggle">
                                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {/* Strength meter */}
                                {password && (
                                    <div className="flex items-center gap-2 mt-2" data-testid="register-pw-strength">
                                        <div className="flex-1 h-1 bg-line overflow-hidden">
                                            <div className={`h-full ${scoreColor} transition-all`}
                                                style={{ width: `${(pwScore.score / 5) * 100}%` }} />
                                        </div>
                                        <span className="text-[10px] font-mono uppercase text-ink2 w-16 text-right">
                                            {pwScore.label}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <Button type="submit" disabled={loading}
                            className="w-full h-11 rounded-none bg-klein hover:bg-kleinDark text-white inline-flex items-center justify-center gap-2 transition-colors"
                            data-testid="register-submit-button">
                            {loading ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Creating account.</>
                            ) : (
                                <>Create account <ArrowRight className="w-4 h-4" /></>
                            )}
                        </Button>
                        <div className="bg-paperWarm border border-line p-3 text-[11px] text-ink2 leading-relaxed">
                            <CheckCircle2 className="w-3.5 h-3.5 text-klein inline mr-1 align-text-bottom" />
                            <strong className="text-ink">Free, no card needed.</strong> You bring your own Anthropic + kie.ai keys. Typical recipe costs ~$0.04 on the provider side.
                        </div>
                        <div className="text-sm text-ink2 text-center border-t border-line pt-5">
                            Already have an account?{" "}
                            <Link to="/login" className="text-klein font-semibold hover:underline"
                                data-testid="register-go-login-link">
                                Sign in
                            </Link>
                        </div>
                    </form>
                </motion.div>
                <div className="p-6 text-[10px] font-mono uppercase text-ink2 text-center border-t border-line">
                    By creating an account you agree to our{" "}
                    <Link to="/terms" className="hover:text-klein">Terms</Link> and{" "}
                    <Link to="/privacy" className="hover:text-klein">Privacy Policy</Link>.
                </div>
            </div>
        </div>
    );
}
